import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, Tabs, Tab, TextField, MenuItem, Button, Chip, IconButton, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, InputAdornment, Autocomplete,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert,
} from '@mui/material';
import {
  LibraryBooks as CatalogIcon, SwapHoriz as CirculationIcon, Add as AddIcon, Edit as EditIcon,
  Delete as DeleteIcon, Search as SearchIcon, AssignmentReturn as ReturnIcon, WarningAmber as OverdueIcon,
  MenuBook as BooksIcon, CollectionsBookmark as CopiesIcon, PaidOutlined as PaidIcon, ReportProblem as LostIcon,
} from '@mui/icons-material';
import { libraryAPI, studentAPI } from '../services/api';
import { StatCard, TabPanel } from './accounting/common';

const errorText = (e, fallback) => e?.response?.data?.message || fallback;
const CATEGORIES = ['NURSING', 'ANATOMY', 'PHYSIOLOGY', 'PHARMACOLOGY', 'GENERAL', 'REFERENCE', 'JOURNAL', 'OTHER'];
const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;
const emptyBookForm = () => ({ title: '', author: '', isbn: '', publisher: '', category: 'NURSING', edition: '', shelfLocation: '', totalCopies: 1, notes: '' });

export default function LibraryManagement() {
  const [tab, setTab] = useState(0);
  const [summary, setSummary] = useState(null);
  const [books, setBooks] = useState([]);
  const [issues, setIssues] = useState([]);
  const [students, setStudents] = useState([]);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('ALL');
  const [issueFilter, setIssueFilter] = useState('ALL');
  const [bookDlg, setBookDlg] = useState(null);
  const [issueDlg, setIssueDlg] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const notify = useCallback((message, severity = 'success') => setSnack({ open: true, message, severity }), []);

  const load = useCallback(async () => {
    try {
      const [s, b, i, st] = await Promise.all([
        libraryAPI.getSummary(), libraryAPI.getBooks({}), libraryAPI.getIssues(), studentAPI.getAll(),
      ]);
      setSummary(s.data); setBooks(b.data); setIssues(i.data); setStudents(st.data);
    } catch (e) { notify(errorText(e, 'Could not load library data'), 'error'); }
  }, [notify]);
  useEffect(() => { load(); }, [load]);

  const shownBooks = books
    .filter((b) => category === 'ALL' || b.category === category)
    .filter((b) => !q || `${b.title} ${b.author || ''} ${b.isbn || ''}`.toLowerCase().includes(q.toLowerCase()));

  const shownIssues = issues.filter((i) => {
    if (issueFilter === 'ALL') return true;
    if (issueFilter === 'OVERDUE') return i.overdue;
    return i.status === issueFilter;
  });

  const openBookDialog = (b = null) => setBookDlg(b ? { ...emptyBookForm(), ...b } : emptyBookForm());

  const saveBook = async () => {
    if (!bookDlg.title.trim()) return notify('Please enter the book title', 'error');
    try {
      const data = { ...bookDlg, totalCopies: Number(bookDlg.totalCopies) || 1 };
      if (bookDlg.id) await libraryAPI.updateBook(bookDlg.id, data); else await libraryAPI.createBook(data);
      notify(bookDlg.id ? 'Book updated' : 'Book added to catalog');
      setBookDlg(null); load();
    } catch (e) { notify(errorText(e, 'Could not save the book'), 'error'); }
  };

  const deleteBook = async () => {
    try { await libraryAPI.deleteBook(confirmDelete.id); notify('Book removed'); setConfirmDelete(null); load(); }
    catch (e) { notify(errorText(e, 'Could not delete'), 'error'); }
  };

  const openIssueDialog = (book = null) => setIssueDlg({ book: book || null, student: null, dueDate: '' });

  const submitIssue = async () => {
    if (!issueDlg.book || !issueDlg.student) return notify('Choose a book and a student', 'error');
    try {
      await libraryAPI.issueBook({ bookId: issueDlg.book.id, studentId: issueDlg.student.id, dueDate: issueDlg.dueDate || undefined });
      notify(`"${issueDlg.book.title}" issued to ${issueDlg.student.firstName} ${issueDlg.student.lastName}`);
      setIssueDlg(null); load();
    } catch (e) { notify(errorText(e, 'Could not issue this book'), 'error'); }
  };

  const returnBook = async (issue) => {
    try { const res = await libraryAPI.returnBook(issue.id); notify(res.data.fineAmount ? `Returned - fine ${money(res.data.fineAmount)}` : 'Returned, no fine'); load(); }
    catch (e) { notify(errorText(e, 'Could not process the return'), 'error'); }
  };
  const markLost = async (issue) => {
    if (!window.confirm(`Mark "${issue.bookTitle}" as lost by ${issue.studentName}?`)) return;
    try { await libraryAPI.markLost(issue.id); notify('Marked as lost'); load(); } catch (e) { notify(errorText(e, 'Could not update'), 'error'); }
  };
  const markFinePaid = async (issue) => {
    try { await libraryAPI.markFinePaid(issue.id); notify('Fine marked as paid'); load(); } catch (e) { notify(errorText(e, 'Could not update'), 'error'); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#004d40' }}>Library</Typography>
          <Typography variant="body2" color="text.secondary">Catalog, issue/return and fines - visible to students in their app</Typography>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard title="Titles" value={summary?.totalTitles ?? 0} icon={BooksIcon} gradient="linear-gradient(135deg,#004d40,#00897b)" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard title="Copies available" value={`${summary?.availableCopies ?? 0}/${summary?.totalCopies ?? 0}`} icon={CopiesIcon} gradient="linear-gradient(135deg,#0288d1,#26c6da)" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard title="Issued now" value={summary?.issuedNow ?? 0} icon={CirculationIcon} gradient="linear-gradient(135deg,#6a1b9a,#ab47bc)" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard title="Overdue" value={summary?.overdueCount ?? 0} icon={OverdueIcon} gradient="linear-gradient(135deg,#c62828,#ef5350)" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard title="Fines pending" value={money(summary?.pendingFines)} icon={PaidIcon} gradient="linear-gradient(135deg,#ef6c00,#ffa726)" />
        </Grid>
      </Grid>

      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ bgcolor: '#f8f9fa', '& .MuiTab-root': { fontWeight: 600, textTransform: 'none' } }}>
          <Tab icon={<CatalogIcon fontSize="small" />} iconPosition="start" label={`Catalog (${books.length})`} />
          <Tab icon={<CirculationIcon fontSize="small" />} iconPosition="start" label={`Circulation (${issues.length})`} />
        </Tabs>

        <Box sx={{ p: 3 }}>
          <TabPanel value={tab} index={0}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField size="small" placeholder="Search title, author, ISBN..." value={q} onChange={(e) => setQ(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
                <TextField select size="small" label="Category" value={category} onChange={(e) => setCategory(e.target.value)} sx={{ minWidth: 180 }}>
                  <MenuItem value="ALL">All categories</MenuItem>
                  {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
              </Box>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => openBookDialog()}>Add book</Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Title</TableCell><TableCell>Author</TableCell><TableCell>Category</TableCell><TableCell>Shelf</TableCell>
                  <TableCell align="center">Copies</TableCell><TableCell align="center">Actions</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {shownBooks.map((b) => (
                    <TableRow key={b.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{b.title}{b.edition ? <Typography variant="caption" display="block" color="text.secondary">{b.edition}</Typography> : null}</TableCell>
                      <TableCell>{b.author || '-'}</TableCell>
                      <TableCell><Chip size="small" label={b.category || 'OTHER'} /></TableCell>
                      <TableCell>{b.shelfLocation || '-'}</TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={`${b.availableCopies}/${b.totalCopies}`} color={b.availableCopies > 0 ? 'success' : 'default'} />
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title={b.availableCopies > 0 ? 'Issue a copy' : 'No copies available'}>
                          <span><IconButton size="small" color="primary" disabled={b.availableCopies < 1} onClick={() => openIssueDialog(b)}><CirculationIcon fontSize="small" /></IconButton></span>
                        </Tooltip>
                        <IconButton size="small" onClick={() => openBookDialog(b)}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => setConfirmDelete(b)}><DeleteIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {shownBooks.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5, color: 'text.secondary' }}>No books in the catalog yet</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel value={tab} index={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {['ALL', 'ISSUED', 'OVERDUE', 'RETURNED', 'LOST'].map((f) => (
                  <Chip key={f} label={f} clickable color={issueFilter === f ? 'primary' : 'default'} onClick={() => setIssueFilter(f)} />
                ))}
              </Box>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => openIssueDialog()}>New issue</Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Book</TableCell><TableCell>Student</TableCell><TableCell>Issued</TableCell><TableCell>Due</TableCell>
                  <TableCell>Status</TableCell><TableCell align="right">Fine</TableCell><TableCell align="center">Actions</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {shownIssues.map((i) => (
                    <TableRow key={i.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{i.bookTitle}</TableCell>
                      <TableCell>{i.studentName}<Typography variant="caption" display="block" color="text.secondary">{i.admissionNo}</Typography></TableCell>
                      <TableCell>{i.issueDate}</TableCell>
                      <TableCell>{i.dueDate}</TableCell>
                      <TableCell>
                        {i.status === 'ISSUED' ? (
                          <Chip size="small" label={i.overdue ? `Overdue ${i.daysOverdue}d` : 'Issued'} color={i.overdue ? 'error' : 'warning'} />
                        ) : (
                          <Chip size="small" label={i.status} color={i.status === 'RETURNED' ? 'success' : 'default'} />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {i.fineAmount ? (
                          <Chip size="small" label={money(i.fineAmount)} color={i.finePaid ? 'success' : 'error'} variant={i.finePaid ? 'filled' : 'outlined'} />
                        ) : '-'}
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        {i.status === 'ISSUED' && (
                          <>
                            <Tooltip title="Mark returned"><IconButton size="small" color="success" onClick={() => returnBook(i)}><ReturnIcon fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title="Mark lost"><IconButton size="small" color="error" onClick={() => markLost(i)}><LostIcon fontSize="small" /></IconButton></Tooltip>
                          </>
                        )}
                        {i.fineAmount && !i.finePaid ? (
                          <Tooltip title="Mark fine paid"><IconButton size="small" color="success" onClick={() => markFinePaid(i)}><PaidIcon fontSize="small" /></IconButton></Tooltip>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                  {shownIssues.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>No records for this filter</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        </Box>
      </Paper>

      {/* Book dialog */}
      <Dialog open={!!bookDlg} onClose={() => setBookDlg(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#004d40', color: 'white' }}>{bookDlg?.id ? 'Edit book' : 'Add book'}</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {bookDlg && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}><TextField fullWidth size="small" label="Title *" value={bookDlg.title} onChange={(e) => setBookDlg({ ...bookDlg, title: e.target.value })} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Author" value={bookDlg.author} onChange={(e) => setBookDlg({ ...bookDlg, author: e.target.value })} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="ISBN" value={bookDlg.isbn} onChange={(e) => setBookDlg({ ...bookDlg, isbn: e.target.value })} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Publisher" value={bookDlg.publisher} onChange={(e) => setBookDlg({ ...bookDlg, publisher: e.target.value })} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Edition" value={bookDlg.edition} onChange={(e) => setBookDlg({ ...bookDlg, edition: e.target.value })} /></Grid>
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth size="small" label="Category" value={bookDlg.category} onChange={(e) => setBookDlg({ ...bookDlg, category: e.target.value })}>
                  {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Shelf location" value={bookDlg.shelfLocation} onChange={(e) => setBookDlg({ ...bookDlg, shelfLocation: e.target.value })} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" type="number" label="Total copies *" inputProps={{ min: 1 }} value={bookDlg.totalCopies} onChange={(e) => setBookDlg({ ...bookDlg, totalCopies: e.target.value })} /></Grid>
              <Grid item xs={12}><TextField fullWidth size="small" multiline rows={2} label="Notes" value={bookDlg.notes} onChange={(e) => setBookDlg({ ...bookDlg, notes: e.target.value })} /></Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setBookDlg(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveBook}>{bookDlg?.id ? 'Update' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* Issue dialog */}
      <Dialog open={!!issueDlg} onClose={() => setIssueDlg(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#004d40', color: 'white' }}>Issue a book</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {issueDlg && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <Autocomplete options={books.filter((b) => b.availableCopies > 0)} value={issueDlg.book}
                  getOptionLabel={(b) => `${b.title}${b.author ? ' - ' + b.author : ''} (${b.availableCopies} available)`}
                  onChange={(e, v) => setIssueDlg({ ...issueDlg, book: v })}
                  renderInput={(params) => <TextField {...params} label="Book" />} />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete options={students} value={issueDlg.student}
                  getOptionLabel={(s) => `${s.firstName} ${s.lastName} (${s.admissionNo})`}
                  onChange={(e, v) => setIssueDlg({ ...issueDlg, student: v })}
                  renderInput={(params) => <TextField {...params} label="Student" />} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" type="date" label="Due date (default 14 days)" InputLabelProps={{ shrink: true }}
                  value={issueDlg.dueDate} onChange={(e) => setIssueDlg({ ...issueDlg, dueDate: e.target.value })} />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setIssueDlg(null)}>Cancel</Button>
          <Button variant="contained" onClick={submitIssue}>Issue</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove book?</DialogTitle>
        <DialogContent><Typography>"{confirmDelete?.title}" will be removed from the catalog. This cannot be undone.</Typography></DialogContent>
        <DialogActions sx={{ p: 2.5 }}><Button onClick={() => setConfirmDelete(null)}>Cancel</Button><Button color="error" variant="contained" onClick={deleteBook}>Delete</Button></DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
